import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { Application } from 'src/app/account/services/application.service'
import { TestPageMockState } from '../../showcase-mock-state'
import { APPLICATION_OVERVIEW_ERROR_ACTIONS } from 'src/app/account/components/application/application.component'
import { InviteUsersModalComponent } from 'src/app/account/components/invite-users-modal/invite-users-modal.component'
import { Subscription, timer } from 'rxjs'
import { ErrorAnchorService } from 'src/app/services/error-anchor.service'
import { UiService } from 'src/app/services/ui.service'
import { PresentedError, toPresentedError } from 'src/utils/presented-error'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { UiService as UiServiceType } from 'src/app/services/ui.service'
import {
  DialogPreview,
  FieldInputPreview,
  LIVE_DIALOG_HOST,
  REAL_DIALOG_HOSTS,
  buildDialogPreviews,
  buildFieldInputPreviews,
  ErrorShowcaseEntry,
  ErrorShowcaseTier,
  buildErrorCodeMap,
  buildErrorSeverities,
  buildErrorTiers,
  buildErrorVariants,
  SHOWCASE_OBJECT_PANEL,
  SHOWCASE_OBJECT_ROW,
} from '../../mocks/showcase-errors-mocks'

const HIDDEN_TIERS = ['tier1', 'tier2', 'tier4', 'tier5']

/** Gap between staggered triggers, long enough to watch each one arrive. */
const STAGGER_MS = 900

/**
 * Every error the console can render, grouped by placement tier, each with a Trigger that
 * replays it through the real `UiService` so a reviewer sees it arrive live rather than as a
 * static block.
 *
 * Tiers 1 to 3 render in their real hosts, so a trigger that reaches the corner instead means
 * the wiring is broken. Tiers 4 and 5 have no host yet, so triggering one shows the toast
 * fallback — the honest current behavior — above the block it is meant to become.
 */
@Component({
  selector: 'showcase-errors',
  templateUrl: './showcase-errors.component.html',
  styleUrls: ['./showcase-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShowcaseErrorsComponent implements AfterViewInit, OnDestroy {
  readonly severities = buildErrorSeverities()
  readonly variants = buildErrorVariants()
  // Tiers 1 and 2 render in their own context below rather than being previewed twice with
  // different fidelity. Tier 3 keeps its place in the list for the triggers, but renders its
  // cases in the frames rather than as static cards. Tiers 4 and 5 are hidden until they have
  // hosts: a block previewed with no surface to land on teaches the wrong thing. Their entries
  // stay in the routing table, which the section's spec still asserts in full.
  readonly tiers = buildErrorTiers().filter((tier) => !HIDDEN_TIERS.includes(tier.key))

  /** Tier 2: each failure inside the dialog that caused it, footer CTA disabled with a reason. */
  readonly dialogPreviews = buildDialogPreviews()

  /** Tier 3: the two hosts the previews below mount, so a trigger lands on its object. */
  readonly objectPanelHost = SHOWCASE_OBJECT_PANEL
  readonly objectRowHost = SHOWCASE_OBJECT_ROW

  // A host drops any remedy it does not list, so without this the live block renders a
  // different action set from the one production runs.
  readonly panelHostActions = APPLICATION_OVERVIEW_ERROR_ACTIONS
  readonly rowHostActions: string[] = []

  @ViewChild('liveDialogModal') private liveDialogModal: ModalDialogComponent

  /** The production dialogs, so their cases anchor in the modal they really ship in. */
  @ViewChild('realInviteUsersModal') private realInviteUsersModal: InviteUsersModalComponent
  @ViewChild('realConfirmActionModal') private realConfirmActionModal: ModalDialogComponent
  @ViewChild('realConcludeRecoveryModal') private realConcludeRecoveryModal: ModalDialogComponent
  @ViewChild('realConcludeTestModal') private realConcludeTestModal: ModalDialogComponent
  @ViewChild('realLaunchTestModal') private realLaunchTestModal: ModalDialogComponent
  @ViewChild('realEditIntegrationModal') private realEditIntegrationModal: ModalDialogComponent

  /** The mock application the three sync-phase dialogs filter their candidate rows on. */
  get mockApplication(): Application | null {
    return this.state.application
  }

  /** Collapsed by default: the section is long, and each subsection is a lot of DOM. */
  readonly openSubsections: Record<string, boolean> = {}

  /** The preview currently open in the real modal. */
  liveDialogPreview: DialogPreview | null = null

  /** The preview currently open in the real confirm-action dialog. */
  confirmActionPreview: DialogPreview | null = null
  readonly codeMap = buildErrorCodeMap()

  /** Total previewed cases, so a reviewer can tell at a glance if coverage dropped. */
  readonly totalCases =
    this.severities.length +
    this.variants.length +
    this.codeMap.length +
    this.tiers.reduce((sum, tier) => sum + tier.entries.length, 0)

  /** Key of the group currently playing, so its button can show progress. */
  playingKey: string | null = null

  private stagger: Subscription = null

  /** One preview per error state, each with the input that produced it. */
  readonly inputPreviews = buildFieldInputPreviews()

  /**
   * One control per preview, so each holds its own error independently and the whole set is
   * legible at once. Every preview is seeded into its error state on init; the Trigger button
   * re-fires it live through the real pipeline.
   */
  readonly inputForm: FormGroup

  /**
   * Field -> control for the host. Retargeted to the preview being triggered, because several
   * previews can share one field: three SAML cases map to `saml-metadata`, and a fixed map
   * sent all of them to the first input, so triggering one preview lit up another.
   */
  readonly inputFieldMap: Record<string, string> = {}

  constructor(
    private readonly uiService: UiService,
    private readonly anchors: ErrorAnchorService,
    private readonly state: TestPageMockState,
    formBuilder: FormBuilder,
    formValidation: FormValidationService
  ) {
    const controls: Record<string, unknown[]> = {}
    for (const preview of this.inputPreviews) {
      controls[preview.controlName] = [preview.value, this.validatorsFor(preview.rules, formValidation, uiService)]
    }
    this.inputForm = formBuilder.group(controls)

    // Seed a default so the map is populated before any trigger; each trigger retargets it.
    for (const preview of this.inputPreviews) {
      if (preview.mode !== 'backend') continue
      const field = toPresentedError(preview.payload).field
      if (field && !this.inputFieldMap[field]) this.inputFieldMap[field] = preview.controlName
    }
  }

  ngAfterViewInit(): void {
    // Deferred past the first pass: binding a control through formControlName runs
    // updateValueAndValidity, which recomputes errors from the validators and would wipe a
    // server error seeded any earlier. Same reason a real backend error clears on edit.
    setTimeout(() => this.seedPreviews())
  }

  /** Puts every preview into the error state it demonstrates. Public so specs can call it. */
  seedPreviews(): void {
    this.inputPreviews.forEach((preview) => this.seed(preview))
  }

  /**
   * Puts a preview into its error state without a round-trip. Local rules produce it by
   * validating the rejected value; a backend case takes its copy from the router, so the
   * static preview and a live trigger cannot say different things.
   */
  private seed(preview: FieldInputPreview): void {
    const control = this.inputForm.get(preview.controlName)
    if (!control) return

    control.markAsTouched()
    if (preview.mode === 'local') return

    const presented = toPresentedError(preview.payload)
    control.setErrors({ ...(control.errors ?? {}), server: { message: presented.detail } })
  }

  /**
   * Real validators wherever the app exposes a shared source (external ID rules and the
   * bucket-name regex), so a trigger reproduces production rather than approximating it.
   */
  private validatorsFor(rules: string, formValidation: FormValidationService, uiService: UiServiceType): ValidatorFn[] {
    switch (rules) {
      case 'external-id':
        return formValidation.getExternalIdValidators()
      case 'bucket-name':
        return [Validators.required, Validators.pattern(uiService.bucketNameRegex)]
      case 'invite-email':
        return [Validators.required, Validators.email]
      case 'password':
        return [Validators.required, Validators.minLength(8)]
      case 'spot-account':
        return [Validators.required, Validators.pattern(/^act-[a-zA-Z0-9]*$/)]
      default:
        return [Validators.required]
    }
  }

  /**
   * Re-fires a preview live. Backend cases go through the public API so the router and the
   * anchor registry both take part — if one of them lands in a toast the field is unmapped.
   * Local cases rewrite the rejected value, which the control's own validator turns into an
   * error again.
   */
  triggerInput(preview: FieldInputPreview): void {
    if (preview.mode === 'backend') {
      // Point the host at this preview's control, or a field with several previews would
      // always claim onto the first one.
      const field = toPresentedError(preview.payload).field
      if (field) this.inputFieldMap[field] = preview.controlName

      this.uiService.announceApiError(preview.payload)
      return
    }

    const control = this.inputForm.get(preview.controlName)
    control?.setValue(preview.value)
    control?.markAsTouched()
  }

  /** Clears the preview so a reviewer can watch the trigger put it back. */
  clearInput(preview: FieldInputPreview): void {
    const control = this.inputForm.get(preview.controlName)
    if (!control) return
    control.setValue(preview.mode === 'local' ? 'valid-again' : preview.value)
    control.setErrors(null)
    control.markAsUntouched()
  }

  resetAllInputs(): void {
    this.inputPreviews.forEach((preview) => this.clearInput(preview))
  }

  /** Local validator copy, mirroring what each modal's own getter renders. */
  localMessage(preview: FieldInputPreview): string | null {
    const control = this.inputForm.get(preview.controlName)
    if (!control?.errors || !control.touched) return null
    if (control.errors['required']) return `${preview.label} is required`
    if (control.errors['email']) return 'Enter a valid email address'
    if (control.errors['minlength']) return 'Must be at least 8 characters'
    if (control.errors['maxlength']) return `${preview.label} is too long`
    if (control.errors['pattern']) return `${preview.label} contains invalid characters`
    return null
  }

  /**
   * Opens a real modal and fires the case's real payload into it. The static frames show the
   * anatomy; this is the proof that a mounted dialog claims the error instead of the corner
   * toast.
   *
   * `ModalDialogComponent` renders its outlet inside `@if (displayed)`, so the host does not
   * exist until the modal is open — hence the deferred fire.
   */
  async openLiveDialog(preview: DialogPreview): Promise<void> {
    // A case whose production dialog is on this page opens that one, so the error lands in
    // the modal it really ships in rather than a look-alike.
    if (preview.realDialog) {
      this.liveDialogPreview = null
      await this.openRealDialog(preview)
      await this.fireInto(REAL_DIALOG_HOSTS[preview.realDialog], preview)
      return
    }

    this.liveDialogPreview = preview
    this.liveDialogModal?.open()
    await this.fireInto(LIVE_DIALOG_HOST, preview)
  }

  /**
   * Opens the production dialog behind a case. The three sync-phase dialogs filter their rows
   * by the anchor application's phase, so each needs the matching mock seeded before it mounts
   * — anchored to the wrong phase they open with no candidates and nothing to fail.
   */
  private async openRealDialog(preview: DialogPreview): Promise<void> {
    if (preview.realDialog === 'invite-users') {
      // InviteUsersModalComponent.open() loads roles and groups before it displays, so this
      // has to be awaited — firing early meant no host existed and the error toasted.
      await this.realInviteUsersModal?.open()
      return
    }
    if (preview.realDialog === 'confirm-action') {
      this.confirmActionPreview = preview
      this.realConfirmActionModal?.open()
      return
    }

    const targets: Record<string, { modal: ModalDialogComponent; app: Application | null }> = {
      'conclude-recovery': { modal: this.realConcludeRecoveryModal, app: this.state.mockFailoverApp },
      'conclude-test': { modal: this.realConcludeTestModal, app: this.state.mockFailoverTestCompletedApp },
      'launch-test': { modal: this.realLaunchTestModal, app: this.state.mockStandbyApp },
      'edit-integration': { modal: this.realEditIntegrationModal, app: null },
    }
    const target = targets[preview.realDialog]
    // A sandbox scenario left over from the Modals section would gate these, same as
    // `openWithApp` there.
    this.state.mockDisabledCapabilities.clear()
    if (target.app) this.state.application = target.app
    // One tick so the swapped application reaches the dialog's inputs before it mounts.
    await new Promise((resolve) => setTimeout(resolve, 0))
    target.modal?.open()
  }

  /**
   * The surface has to be named on the call or the registry won't offer it the error — a
   * surface-tier error only reaches the host the call site asked for. Firing without the id
   * opened a real dialog and then toasted behind it, which looked like the anchor was broken.
   */
  private async fireInto(hostId: string, preview: DialogPreview): Promise<void> {
    await this.whenHostRegistered(hostId)
    this.uiService.announceApiError(preview.payload, undefined, { dialogId: hostId })
  }

  /**
   * A dialog's host only exists once its outlet has rendered, which is a tick after `open()`
   * at best and later when the dialog loads data first. Waiting on the registry removes the
   * guess; the bound attempts stop a missing host from hanging the trigger.
   */
  private whenHostRegistered(id: string, attempts = 40): Promise<void> {
    return new Promise((resolve) => {
      const poll = (left: number) => {
        if (left <= 0 || this.anchors.registeredIds.includes(id)) {
          resolve()
          return
        }
        setTimeout(() => poll(left - 1), 25)
      }
      poll(attempts)
    })
  }

  toggleSubsection(key: string): void {
    this.openSubsections[key] = !this.openSubsections[key]
  }

  closeLiveDialog(): void {
    this.liveDialogModal?.close()
    this.liveDialogPreview = null
  }

  closeConfirmActionDialog(): void {
    this.realConfirmActionModal?.close()
    this.confirmActionPreview = null
  }

  inputTrackBy(_index: number, preview: FieldInputPreview): string {
    return preview.key
  }

  dialogTrackBy(_index: number, preview: DialogPreview): string {
    return preview.key
  }

  ngOnDestroy(): void {
    this.stagger?.unsubscribe()
  }

  /**
   * Replays one case through the public API the app actually calls, so the router, the shell
   * stack and the dedup rule all take part. Entries whose tier supplies a context go through
   * `present` instead — that is how an anchored host will raise them.
   */
  trigger(entry: ErrorShowcaseEntry): void {
    const raw = entry.raw as { responseCode?: number }

    // An entry that names a mounted host goes through the public path, so the registry decides
    // whether it anchors. One that only models an anchor has no host to ask, so it is presented
    // directly — asking would tier it differently from the block above it.
    if (entry.context.objectId || entry.context.dialogId) {
      this.uiService.announceApiError(entry.raw, undefined, entry.context)
      return
    }

    if (Object.keys(entry.context).length > 0) {
      this.uiService.present(toPresentedError(entry.raw, entry.context))
      return
    }

    if (raw?.responseCode !== undefined) {
      this.uiService.announceApiError(entry.raw)
      return
    }

    this.uiService.announceError(entry.raw as never)
  }

  /**
   * Fires a whole group in sequence rather than at once. Simultaneous triggers would be
   * capped at three and deduped, which hides the arrival behavior this button exists to show.
   */
  play(key: string, entries: ErrorShowcaseEntry[]): void {
    this.stagger?.unsubscribe()
    this.playingKey = key

    this.stagger = timer(0, STAGGER_MS).subscribe((index) => {
      if (index >= entries.length) {
        this.stagger.unsubscribe()
        this.playingKey = null
        return
      }
      this.trigger(entries[index])
    })
  }

  /** The preview's stand-in for the remedy running, so no rendered button is dead. */
  onPreviewAction(host: { clear: () => void }): void {
    host.clear()
  }

  stop(): void {
    this.stagger?.unsubscribe()
    this.playingKey = null
  }

  entryTrackBy(_index: number, entry: ErrorShowcaseEntry): string {
    return entry.key
  }

  tierTrackBy(_index: number, tier: ErrorShowcaseTier): string {
    return tier.key
  }
}
